<script lang="ts" module>
	const STORAGE_KEY = "commarec-newsletter-bar";
	const APPEAR_DELAY_MS = 3000;
	const SUCCESS_HIDE_DELAY_MS = 4000;
</script>

<script lang="ts">
	import { subscribeToNewsletter } from "@api/subscribeToNewsletter.js";
	import Alert from "@components/Alert.svelte";
	import type { AlertType } from "@components/AlertType.js";
	import RecaptchaDisclaimer from "@components/RecaptchaDisclaimer.svelte";
	import Arrow from "@components/common/Arrow.svelte";
	import Button from "@components/common/Button.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Form from "@components/form/Form.svelte";
	import LabelledInput from "@components/form/LabelledInput.svelte";
	import { fly } from "svelte/transition";
	import check from "@core/assets/icons/check.svg?raw";
	import close from "@core/assets/icons/close.svg?raw";
	import loadingIcon from "@core/assets/icons/loading-icon.svg?raw";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { NewsletterSource } from "@core/schema/NewsletterSource.js";
	import { getGdprPath } from "@core/schema/paths/getGdprPath.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";

	const { PUBLIC_GRAPHQL_ENDPOINT } = getGlobalSettings();
	const { tenant } = getGlobalTemplateProps();
	const gdprId = $props.id();

	let visible = $state(false);
	let loading = $state(false);
	let feedback = $state<{ type: AlertType; text: string }>();
	let barEl = $state<HTMLDivElement>();

	$effect(() => {
		if (localStorage.getItem(STORAGE_KEY)) {
			return;
		}
		const id = setTimeout(() => {
			visible = true;
		}, APPEAR_DELAY_MS);
		return (): void => {
			clearTimeout(id);
		};
	});

	// iOS Safari positions `fixed; bottom: 0` against the layout viewport, not the
	// visual one — so the bar detaches (a gap appears) when the bottom toolbar
	// shows/hides on scroll or when the keyboard opens. Glue it to the visual
	// viewport bottom instead so it always sits flush above the toolbar/keyboard.
	$effect(() => {
		const el = barEl;
		const vv = window.visualViewport;
		if (!el || !vv) {
			return;
		}
		const update = (): void => {
			const offset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
			el.style.bottom = `${offset}px`;
		};
		update();
		vv.addEventListener("resize", update);
		vv.addEventListener("scroll", update);
		return (): void => {
			vv.removeEventListener("resize", update);
			vv.removeEventListener("scroll", update);
		};
	});

	// The cookie-script.com consent badge floats bottom-left above everything and
	// would overlap this bar. While the bar is shown, expose its measured height
	// so global CSS can lift the badge above it (keep it updated as the bar reflows
	// across breakpoints or when feedback appears).
	$effect(() => {
		const el = barEl;
		if (!visible || !el) {
			return;
		}
		const root = document.documentElement;
		const updateHeight = (): void => {
			root.style.setProperty("--newsletter-bar-height", `${Math.ceil(el.getBoundingClientRect().height)}px`);
		};
		updateHeight();
		root.classList.add("newsletter-bar-open");
		const observer = new ResizeObserver(updateHeight);
		observer.observe(el);
		return (): void => {
			observer.disconnect();
			root.classList.remove("newsletter-bar-open");
			root.style.removeProperty("--newsletter-bar-height");
		};
	});

	function dismiss(): void {
		visible = false;
		localStorage.setItem(STORAGE_KEY, "dismissed");
	}

	async function submitNewsletterForm(event: SubmitEvent, form: HTMLFormElement): Promise<void> {
		event.preventDefault();
		const formData = new FormData(form);
		loading = true;
		try {
			await subscribeToNewsletter(PUBLIC_GRAPHQL_ENDPOINT, {
				email: formData.get("email") as string,
				gdprApprovedAt: new Date().toISOString(),
				locale: tenant.locale,
				source: NewsletterSource.PopupBar,
			});
			feedback = {
				text: "Děkujeme, přihlášení k odběru newsletteru proběhlo úspěšně.",
				type: "success",
			};
			localStorage.setItem(STORAGE_KEY, "subscribed");
			form.reset();
			setTimeout(() => {
				visible = false;
			}, SUCCESS_HIDE_DELAY_MS);
		} catch (error) {
			feedback = {
				text: "Přihlášení se nepodařilo. Prosíme vyzkoušejte to za chvíli znovu.",
				type: "warning",
			};
			throw error;
		} finally {
			loading = false;
		}
	}
</script>

{#snippet buttonIcon()}
	{#if loading}
		<Icon icon={loadingIcon} class="size-4 animate-spin" />
	{:else}
		<Arrow class="size-4" />
	{/if}
{/snippet}

{#if visible}
	<div
		bind:this={barEl}
		transition:fly={{ y: 100, duration: 400 }}
		class="fixed bottom-0 z-40 inset-x-0 border-t border-white/10 bg-cmcnavy pb-[env(safe-area-inset-bottom)] text-white shadow-[0_-8px_32px_-12px_rgba(0,0,0,0.35)]"
	>
		<div class="relative mx-auto w-full max-w-[82rem] px-4 py-2.5 pr-11 sm:px-6 sm:pr-12">
			<div class="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:gap-6">
				<div class="min-w-0 lg:max-w-[16rem] lg:shrink-0">
					<p class="text-sm font-bold leading-tight">
						<Formatted text="Odebírejte náš newsletter" />
					</p>
					<p class="mt-0.5 hidden text-2xs/tight text-white/55 sm:block">
						<Formatted text="Novinky ze světa skladování a logistiky, přímo do schránky." />
					</p>
				</div>

				<Form
					class="flex min-w-0 flex-1 flex-col gap-x-5 gap-y-2 sm:flex-row sm:flex-wrap sm:items-center lg:flex-nowrap lg:items-center lg:justify-end lg:gap-4"
					onsubmit={submitNewsletterForm}
				>
					<div class="flex w-full flex-col gap-2 sm:w-auto sm:flex-1 sm:basis-72 sm:flex-row lg:max-w-xl">
						<LabelledInput
							required
							type="email"
							name="email"
							value=""
							placeholder="Zadejte Váš e-mail"
							wrapperClass="min-w-0 flex-1"
							label={{ class: "sr-only" }}
							class="!h-9 !min-h-0 !rounded-lg !border-cmcnavy-80 !bg-white/95 !px-3 !py-0 !text-sm"
						>
							E-mail
						</LabelledInput>
						<Button
							icon={buttonIcon}
							disabled={loading}
							variant={ButtonVariant.Primary}
							class="h-9 w-full shrink-0 !gap-2 !rounded-lg !px-4 !text-xs sm:w-auto"
						>
							<Formatted text={loading ? "Odesílám" : "Odebírat"} />
						</Button>
					</div>

					<label for={gdprId} class="flex cursor-pointer items-center gap-2 sm:shrink-0">
						<span class="relative block size-4 shrink-0">
							<input
								id={gdprId}
								type="checkbox"
								name="gdpr"
								required
								class="block size-4 cursor-pointer appearance-none rounded-[3px] border-[1.5px] border-cmclightgray bg-white"
							/>
							<span
								class="pointer-events-none absolute inset-0 flex items-center justify-center [:not(:checked)+&]:hidden"
							>
								<Icon icon={check} class="size-4 text-cmcprimary" />
							</span>
						</span>
						<span class="text-2xs/tight tracking-[0.02em] text-white/85">
							<Formatted text="Souhlasím se l[{getGdprPath(tenant)}][[zpracováním osobních údajů]]" />
						</span>
					</label>
				</Form>
			</div>

			<RecaptchaDisclaimer
				class="!mt-1.5 !text-left !text-[0.5625rem] !leading-tight text-white/40 md:!text-[0.5625rem] [&_a]:underline"
			/>

			{#if feedback}
				<div class="mt-2">
					<Alert type={feedback.type}>
						<Formatted text={feedback.text} />
					</Alert>
				</div>
			{/if}
		</div>

		<button
			type="button"
			class="absolute right-2 top-2 flex size-8 items-center justify-center text-white/60 transition-colors hover:text-white sm:right-3 lg:top-1/2 lg:-translate-y-1/2"
			aria-label="Zavřít"
			onclick={dismiss}
		>
			<Icon icon={close} class="size-4" />
		</button>
	</div>
{/if}
